import React, { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { Trash2Icon, PencilIcon, Loader2Icon } from 'lucide-react';
import {
    useMakesByIds,
    useModelsByIds,
    useDerivativesByIds,
    useTransmissionsByIds,
    useFuelTypesByIds,
    buildNameMap,
    buildDerivativeNameMap,
} from '@/features/vehiclesearch/hooks/useVehicleNameLookups';
import type { PlanVehicleRule } from '../types/planVehicleRule.types';

function formatRange(min: number | null, max: number | null): string {
    if (min === null && max === null) return 'Any';
    if (min !== null && max !== null) return `${min}–${max}`;
    if (min !== null) return `${min}+`;
    return `–${max}`;
}

interface Props {
    data: PlanVehicleRule[];
    onEdit: (rule: PlanVehicleRule) => void;
    onRemove: (rule: PlanVehicleRule) => void;
    isLoading?: boolean;
    removingId?: string | null;
    /** Freezes the row actions while a save is in flight, so no row switch can outrun it. */
    disableActions?: boolean;
}

export const PlanVehicleRuleDataTable: React.FC<Props> = ({
    data,
    onEdit,
    onRemove,
    isLoading = false,
    removingId = null,
    disableActions = false,
}) => {
    const makeIds         = data.map((r) => r.make_id);
    const modelIds        = data.map((r) => r.model_id);
    const derivativeIds   = data.map((r) => r.derivative_id);
    const transmissionIds = data.map((r) => r.transmission_id);
    const fuelTypeIds     = data.map((r) => r.fuel_type_id);

    const { data: makes }         = useMakesByIds(makeIds);
    const { data: models }        = useModelsByIds(modelIds);
    const { data: derivatives }   = useDerivativesByIds(derivativeIds);
    const { data: transmissions } = useTransmissionsByIds(transmissionIds);
    const { data: fuelTypes }     = useFuelTypesByIds(fuelTypeIds);

    const makeMap         = buildNameMap(makes);
    const modelMap        = buildNameMap(models);
    const derivativeMap   = buildDerivativeNameMap(derivatives);
    const transmissionMap = buildNameMap(transmissions);
    const fuelTypeMap     = buildNameMap(fuelTypes);

    const columns = useMemo<ColumnDef<PlanVehicleRule>[]>(() => [
        {
            id: 'make',
            header: 'Make',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {row.make_id ? (makeMap.get(row.make_id) ?? '—') : 'Any'}
                    </span>
                );
            },
        },
        {
            id: 'model',
            header: 'Model',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                const text = row.derivative_id
                    ? '—'
                    : row.model_id ? (modelMap.get(row.model_id) ?? '—') : 'Any';
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {text}
                    </span>
                );
            },
        },
        {
            id: 'derivative',
            header: 'Derivative',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {row.derivative_id ? (derivativeMap.get(row.derivative_id) ?? '—') : 'Any'}
                    </span>
                );
            },
        },
        {
            id: 'engine',
            header: 'Engine (cc)',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {row.derivative_id ? '—' : formatRange(row.engine_min, row.engine_max)}
                    </span>
                );
            },
        },
        {
            id: 'transmission',
            header: 'Transmission',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                const text = row.derivative_id
                    ? '—'
                    : row.transmission_id ? (transmissionMap.get(row.transmission_id) ?? '—') : 'Any';
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {text}
                    </span>
                );
            },
        },
        {
            id: 'fuel_type',
            header: 'Fuel Type',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                const text = row.derivative_id
                    ? '—'
                    : row.fuel_type_id ? (fuelTypeMap.get(row.fuel_type_id) ?? '—') : 'Any';
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {text}
                    </span>
                );
            },
        },
        {
            id: 'age',
            header: 'Age (yrs)',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                return (
                    <span className={`text-sm${busy ? ' opacity-40' : ''}`}>
                        {row.derivative_id ? '—' : formatRange(row.age_min, row.age_max)}
                    </span>
                );
            },
        },
        {
            id: 'requires_4x4',
            header: '4x4',
            cell: (info) => {
                const row  = info.row.original;
                const busy = row.id === removingId;
                if (row.derivative_id) {
                    return <span className={`text-sm${busy ? ' opacity-40' : ''}`}>—</span>;
                }
                if (row.requires_4x4 === null || row.requires_4x4 === undefined) {
                    return <span className={`text-sm${busy ? ' opacity-40' : ''}`}>Any</span>;
                }
                return (
                    <span className={busy ? 'opacity-40' : undefined}>
                        <Badge variant="outline">{row.requires_4x4 ? '4x4 only' : 'Non-4x4 only'}</Badge>
                    </span>
                );
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const row        = info.row.original;
                const isRemoving = row.id === removingId;

                if (isRemoving) {
                    return <Loader2Icon size={18} className="animate-spin text-mp-muted" />;
                }

                return (
                    <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                        {/* Anchors focus restore: the panel re-finds this row's pencil by rule id. */}
                        <div className="flex space-x-1" data-rule-actions={row.id}>
                            <Tooltip content="Edit rule">
                                <TextLink
                                    color="secondary"
                                    onClick={() => onEdit(row)}
                                    disabled={removingId !== null || disableActions}
                                    startIcon={<PencilIcon size={18} />}
                                    ariaLabel="Edit rule"
                                />
                            </Tooltip>
                            <Tooltip content="Remove rule">
                                <TextLink
                                    color="error"
                                    onClick={() => onRemove(row)}
                                    disabled={removingId !== null || disableActions}
                                    startIcon={<Trash2Icon size={18} />}
                                    ariaLabel="Remove rule"
                                />
                            </Tooltip>
                        </div>
                    </PermissionGuard>
                );
            },
        },
    ], [removingId, disableActions, makeMap, modelMap, derivativeMap, transmissionMap, fuelTypeMap, onEdit, onRemove]);

    if (isLoading) {
        return <LoadingSpinner size="lg" className="py-8" message="Loading vehicle rules..." />;
    }

    return <Table<PlanVehicleRule> data={data} columns={columns} enableSorting={false} />;
};
